import { useState } from "react";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { FlightPath, routeProgress, sectionStops } from "@/components/applicant/FlightPath";
import { SaveLater } from "@/components/applicant/SaveLater";

/** Top of every step screen: Back, the route to the new home, and the section this step belongs to. */
export const StepHeader = ({ steps, index, onBack, saveUrl, phase = 0 }) => {
  const total = steps.length;
  return (
    <div className="mb-8">
      <div className="flex h-9 items-center justify-between">
        {onBack ? (
          <Button variant="ghost" size="sm" className="-ml-2.5 text-muted-foreground" onClick={onBack} data-testid="step-back-button"><ArrowLeft /> Back</Button>
        ) : <span />}
        {saveUrl && <SaveLater url={saveUrl} />}
      </div>
      <FlightPath t={routeProgress(steps, index, phase)} stops={sectionStops(steps)} className="mt-3" />
      <p className="mt-4 text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground" data-testid="step-section">{steps[index].section}</p>
      <span className="sr-only" data-testid="step-progress">Step {index + 1} of {total}</span>
    </div>
  );
};

export const StepTitle = ({ children, sub, testid }) => (
  <div>
    <h1 className="text-[26px] font-semibold leading-tight tracking-tight" data-testid={testid}>{children}</h1>
    {sub && <p className="mt-2 text-base leading-relaxed text-muted-foreground">{sub}</p>}
  </div>
);

/** The step's buttons, pinned to the bottom of the screen on a phone. */
export const StepActions = ({ children, className }) => (
  <div className={cn("sticky bottom-0 z-10 -mx-5 mt-8 flex flex-col gap-2 bg-gradient-to-t from-panel from-70% to-transparent px-5 pb-5 pt-6 md:static md:mx-0 md:bg-none md:px-0 md:pb-0 md:pt-0", className)}>
    {children}
  </div>
);

export const QuestionStep = ({ q, current, onAnswer }) => {
  const [busy, setBusy] = useState(null);
  const pick = async (value) => {
    setBusy(value);
    await onAnswer(q.key, value);
    setBusy(null);
  };
  return (
    <div key={q.key} className="rise" data-testid={`question-${q.key}`}>
      <StepTitle sub={q.hint}>{q.title}</StepTitle>
      <div className="mt-8 flex flex-col gap-3" role="radiogroup" aria-label={q.title}>
        {q.options.map((o) => {
          const selected = current === o.value;
          return (
            <button key={o.value} type="button" role="radio" aria-checked={selected} onClick={() => pick(o.value)} disabled={!!busy}
              className={cn("flex min-h-[64px] w-full items-center gap-4 rounded-2xl border bg-background px-5 py-4 text-left transition-[border-color,box-shadow,transform] active:scale-[0.99] disabled:opacity-100",
                selected ? "border-foreground shadow-[0_0_0_1px_hsl(var(--foreground))]" : "hover:border-foreground/40")}
              data-testid={`answer-${q.key}-${o.value}`}>
              <span className={cn("grid size-5 shrink-0 place-items-center rounded-full border-[1.5px] transition-colors",
                selected ? "border-foreground bg-foreground text-background" : "border-muted-foreground/50")}>
                {busy === o.value ? <Loader2 className="size-3 animate-spin" /> : selected && <Check className="size-3" strokeWidth={3} />}
              </span>
              <span className="min-w-0">
                <span className="block text-base font-medium">{o.label}</span>
                {o.sub && <span className="block text-sm text-muted-foreground">{o.sub}</span>}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
